import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { API_URL, useAuth } from '@/contexts/AuthContext';
import SyncDimensoesCard from '@/components/SyncDimensoesCard';
import TabelaVendedores from '@/components/TabelaVendedores';
import { PageCover, PageBody, CoverButton, SheetHeader, Segmented, StatusDot } from '@/components/Page';
import { effectiveWorkflow, getWorkflowConfig } from '@/components/WorkflowStatusChips';
import {
  formatBRL, formatBRLCompacto, formatNumero, formatDia, formatDiaDaSemana, formatDiaHora, formatHora, formatMes,
  formatMesCurto,
} from '@/lib/format';
import { tomDoPedido } from '@/lib/pagamento';

const PERIODOS_DASH = [
  { value: 'hoje', label: 'Hoje' },
  { value: 'mes', label: 'Este mês' },
  { value: '30d', label: '30 dias' },
  { value: 'tudo', label: 'Tudo' },
];

// Status em uma linha que encurta com reticências quando a coluna aperta (o StatusDot sozinho quebraria a linha).
// O tom segue o pedido (financiamento no Tiny e não pago anda, não concluiu).
const StatusCurto = ({ wf, pedido }) => {
  const cfg = getWorkflowConfig(wf);
  return (
    <StatusDot tone={tomDoPedido(pedido, cfg.tone)} className="min-w-0 text-[12.5px]">
      <span className="truncate" title={cfg.label}>{cfg.label}</span>
    </StatusDot>
  );
};

// Barra das etapas agrupada por tom (quem age). Ordem e passos passaram no validador da
// skill de dataviz: vizinhos distintos também para daltonismo e contraste >= 3:1 sobre a folha.
const TONS = [
  { tom: 'ok', rotulo: 'Concluídos', cor: 'bg-green-600 dark:bg-green-500' },
  { tom: 'run', rotulo: 'Andando sozinhos', cor: 'bg-blue-500' },
  { tom: 'att', rotulo: 'Esperando alguém', cor: 'bg-amber-600' },
  { tom: 'off', rotulo: 'Encerrados', cor: 'bg-gray-500' },
  { tom: 'bad', rotulo: 'Travados', cor: 'bg-red-600 dark:bg-red-500' },
];
const COR_TOM = Object.fromEntries(TONS.map((t) => [t.tom, t.cor]));

// Mesma grade nas duas faixas, para os vãos entre as folhas se alinharem. Até 1535px a primeira coluna
// (etapas) ganha um pouco mais de largura, senão os nomes das etapas saem cortados; daí em diante, terços.
const COLUNAS = 'xl:grid-cols-[5fr_4fr_4fr] 2xl:grid-cols-3';
const NOME_GRANULARIDADE = { hora: 'hora', dia: 'dia', mes: 'mês' };

// Chave da série: 'hh' (hora), 'aaaa-mm' (mês) ou 'aaaa-mm-dd' (dia), sempre no horário de Brasília.
const rotuloCurto = (chave, gran) => {
  if (gran === 'hora') return `${chave}h`;
  if (gran === 'mes') return formatMesCurto(`${chave}-01`);
  return formatDia(chave);
};

const rotuloLongo = (chave, gran) => {
  if (gran === 'hora') return `hoje, ${chave}h`;
  if (gran === 'mes') return formatMes(`${chave}-01`);
  return formatDiaDaSemana(chave);
};

// Topo do eixo em número redondo (1, 2, 2,5 ou 5 x 10^n) acima do maior valor.
const topoDoEixo = (max) => {
  if (max <= 0) return 0;
  const base = 10 ** Math.floor(Math.log10(max));
  return [1, 2, 2.5, 5, 10].map((m) => m * base).find((v) => v >= max);
};

const rotuloEixo = (v) => {
  if (v >= 1e6) return `${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  if (v >= 1e3) return `${Math.round(v / 1e3).toLocaleString('pt-BR')} mil`;
  return formatNumero(Math.round(v));
};

const plural = (n, um, varios) => `${formatNumero(n)} ${n === 1 ? um : varios}`;

const Esqueleto = ({ altura }) => <div aria-hidden className={`m-5 animate-pulse rounded-lg bg-muted ${altura}`} />;

// Colunas finas (60% da faixa, até 24px) saindo da base, com ar entre elas, grade discreta; o valor
// aparece ao passar o mouse ou com as setas do teclado, e uma tabela oculta lê tudo.
const GraficoVendas = ({ serie, gran }) => {
  const [ativo, setAtivo] = useState(null);
  const n = serie.length;
  const topo = topoDoEixo(Math.max(0, ...serie.map((s) => s.valor)));
  if (!n || !topo) return <p className="px-5 py-12 text-sm text-gray-500">Nenhuma venda no período.</p>;

  const marcas = [...new Set([0, Math.floor((n - 1) / 2), n - 1])];
  const ponto = ativo != null ? serie[ativo] : null;
  const mover = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const passo = e.key === 'ArrowRight' ? 1 : -1;
    setAtivo((i) => (i == null ? n - 1 : Math.min(n - 1, Math.max(0, i + passo))));
  };

  // A área do gráfico cresce até a altura da faixa (quando a folha ao lado é mais alta), nunca abaixo de 224px.
  return (
    <div className="flex flex-1 flex-col px-5 pb-5 pt-8">
      <div
        role="group"
        tabIndex={0}
        aria-label="Gráfico de vendas. Use as setas para ler cada ponto."
        onKeyDown={mover}
        onBlur={() => setAtivo(null)}
        className="flex flex-1 flex-col rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-card"
      >
        <div className="relative ml-12 min-h-56 flex-1" onMouseLeave={() => setAtivo(null)}>
          {[1, 0.5, 0].map((f) => (
            <div key={f} aria-hidden className="absolute inset-x-0 border-t border-border" style={{ bottom: `${f * 100}%` }}>
              <span className="absolute -left-12 -top-2 w-10 text-right text-[11px] tabular-nums text-gray-500">{rotuloEixo(topo * f)}</span>
            </div>
          ))}
          <div className="absolute inset-0 flex items-end gap-[2px]">
            {serie.map((s, i) => (
              <div key={s.chave} className="flex h-full flex-1 items-end justify-center" onMouseEnter={() => setAtivo(i)}>
                <div
                  className={`w-3/5 max-w-[24px] rounded-t-[4px] bg-primary transition-opacity duration-150 ${ativo != null && ativo !== i ? 'opacity-40' : ''}`}
                  style={{ height: s.valor > 0 ? `max(2px, ${(s.valor / topo) * 100}%)` : 0 }}
                />
              </div>
            ))}
          </div>
          {ponto && (
            <div
              role="status"
              className="pointer-events-none absolute bottom-full z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded-lg border border-border bg-popover px-3 py-2 shadow-pop"
              style={{ left: `clamp(88px, ${((ativo + 0.5) / n) * 100}%, calc(100% - 88px))` }}
            >
              <p className="text-sm font-semibold tabular-nums">{formatBRL(ponto.valor)}</p>
              <p className="text-[12px] text-gray-500">{rotuloLongo(ponto.chave, gran)} · {plural(ponto.pedidos, 'pedido', 'pedidos')}</p>
            </div>
          )}
        </div>
        <div aria-hidden className="relative ml-12 mt-2 h-4 text-[11px] tabular-nums text-gray-500">
          {marcas.map((i) => (
            <span key={i} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${((i + 0.5) / n) * 100}%` }}>
              {rotuloCurto(serie[i].chave, gran)}
            </span>
          ))}
        </div>
      </div>
      <table className="sr-only">
        <caption>Vendas por {NOME_GRANULARIDADE[gran]}</caption>
        <thead><tr><th>Quando</th><th>Vendido</th><th>Pedidos</th></tr></thead>
        <tbody>
          {serie.map((s) => (
            <tr key={s.chave}><td>{rotuloLongo(s.chave, gran)}</td><td>{formatBRL(s.valor)}</td><td>{s.pedidos}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Uma barra empilhada por tom e, embaixo, cada etapa com quantidade e valor (a lista é a legenda).
const EtapasPedidos = ({ etapas }) => {
  const [tomAtivo, setTomAtivo] = useState(null);
  const porTom = TONS.map((t) => ({
    ...t,
    pedidos: etapas.filter((e) => getWorkflowConfig(e.etapa).tone === t.tom).reduce((s, e) => s + e.pedidos, 0),
  })).filter((t) => t.pedidos > 0);
  if (!porTom.length) return <p className="px-5 py-10 text-sm text-gray-500">Nenhum pedido no período.</p>;

  // A API manda as oito etapas (as vazias com zero) e as linhas dividem a altura da faixa:
  // a folha termina cheia, sem vão embaixo, qualquer que seja o número de etapas com pedido.
  return (
    <div className="flex flex-1 flex-col px-5 pb-4 pt-5">
      <div className="flex h-3 gap-[2px] overflow-hidden rounded-[4px]" role="img" aria-label={porTom.map((t) => `${t.rotulo}: ${t.pedidos}`).join(', ')}>
        {porTom.map((t) => (
          <div
            key={t.tom}
            title={`${t.rotulo}: ${plural(t.pedidos, 'pedido', 'pedidos')}`}
            onMouseEnter={() => setTomAtivo(t.tom)}
            onMouseLeave={() => setTomAtivo(null)}
            className={`${t.cor} transition-opacity duration-150 ${tomAtivo && tomAtivo !== t.tom ? 'opacity-40' : ''}`}
            style={{ flexGrow: t.pedidos, flexBasis: 0 }}
          />
        ))}
      </div>
      <ul className="mt-4 flex flex-1 flex-col divide-y divide-border">
        {etapas.map((e) => {
          const cfg = getWorkflowConfig(e.etapa);
          const vazia = e.pedidos === 0;
          return (
            <li
              key={e.etapa}
              onMouseEnter={vazia ? undefined : () => setTomAtivo(cfg.tone)}
              onMouseLeave={vazia ? undefined : () => setTomAtivo(null)}
              className={`-mx-2 grid flex-1 grid-cols-[minmax(0,1fr)_auto_5rem] items-center gap-x-3 rounded-md px-2 py-2.5 text-[13px] transition-colors duration-150 sm:gap-x-4 sm:text-sm ${vazia ? 'text-gray-500' : ''} ${tomAtivo === cfg.tone ? 'bg-accent' : ''}`}
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <span aria-hidden className={`h-2 w-2 shrink-0 rounded-[2px] ${COR_TOM[cfg.tone]} ${vazia ? 'opacity-40' : ''}`} />
                <span className="truncate" title={cfg.label}>{cfg.label}</span>
              </span>
              {/* só o número: a coluna cabe em um terço da tela; o título da folha já diz que são pedidos */}
              <span className="text-right tabular-nums text-gray-500">
                {formatNumero(e.pedidos)}<span className="sr-only"> {e.pedidos === 1 ? 'pedido' : 'pedidos'}</span>
              </span>
              <span className={`text-right tabular-nums ${vazia ? '' : 'font-medium'}`}>
                {vazia ? <span aria-hidden>–</span> : formatBRLCompacto(e.valor)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// Duas linhas por pedido (cliente e valor; status e quando) para caber em meia tela. O número do pedido
// some só no celular: ali ele empurraria o status.
const UltimosPedidos = ({ pedidos }) => {
  if (!pedidos.length) return <p className="px-5 py-10 text-sm text-gray-500">Nenhum pedido ainda.</p>;
  // As linhas dividem a altura da faixa quando a folha ao lado (etapas) é mais alta; o teto de 96px
  // impede que um vendedor com 1 ou 2 pedidos veja a linha esticada flutuando no meio da folha.
  return (
    <ul className="flex flex-1 flex-col divide-y divide-border">
      {pedidos.map((p) => (
        <li key={p.id} className="flex max-h-24 flex-1 flex-col justify-center gap-1 px-5 py-3">
          <div className="flex items-baseline justify-between gap-4">
            <span className="min-w-0 truncate text-sm font-medium" title={p.cliente_nome}>{p.cliente_nome}</span>
            <span className="shrink-0 text-sm font-medium tabular-nums">{formatBRL(p.total)}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <StatusCurto wf={effectiveWorkflow(p)} pedido={p} />
            <span className="shrink-0 whitespace-nowrap text-[12px] tabular-nums text-gray-500">
              {p.numero_pedido && <span className="hidden sm:inline">{p.numero_pedido} · </span>}
              {formatDiaHora(p.created_at)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const isMaster = user?.role === 'master';
  const isAdministrativo = user?.role === 'administrativo' || isMaster;
  // Veem todos os pedidos e a tabela por vendedor (o servidor já manda os vendedores ao Sales Ops); o comercial
  // vê só os próprios números no topo.
  const veTodos = isAdministrativo || user?.role === 'sales_ops';
  const [periodo, setPeriodo] = useState('mes');
  const [kpis, setKpis] = useState(null);
  const [painel, setPainel] = useState(null);
  const [painelFalhou, setPainelFalhou] = useState(false);
  const [recarregando, setRecarregando] = useState(false);
  const [pedidos, setPedidos] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  // Guarda a última lista para comparar no polling (o aviso de "foi pago" antes lia um estado velho e nunca disparava).
  const pedidosRef = useRef([]);

  const fetchPedidos = useCallback(async (silencioso = false) => {
    try {
      const { data } = await axios.get(`${API_URL}/pedidos`);
      if (silencioso) {
        const antes = new Map(pedidosRef.current.map((p) => [p.id, p.payment_status]));
        data.forEach((p) => {
          const anterior = antes.get(p.id);
          if (anterior && anterior !== p.payment_status && ['confirmed', 'paid'].includes(p.payment_status)) {
            toast.success(`Pedido ${p.numero_pedido} foi pago.`, { duration: 5000 });
          }
        });
      }
      pedidosRef.current = data;
      setPedidos(data);
      setLastUpdate(new Date());
    } catch {
      if (!silencioso) toast.error('Erro ao carregar pedidos');
    }
  }, []);

  // Números da capa e do painel: base inteira do período (a lista acima é só dos mais recentes).
  const fetchNumeros = useCallback(async (silencioso = false) => {
    setRecarregando(true);
    const [k, p] = await Promise.allSettled([
      axios.get(`${API_URL}/pedidos/kpis`, { params: { periodo } }),
      axios.get(`${API_URL}/pedidos/painel`, { params: { periodo } }),
    ]);
    if (k.status === 'fulfilled') setKpis(k.value.data);
    if (p.status === 'fulfilled') {
      setPainel(p.value.data);
      setPainelFalhou(false);
    } else {
      setPainelFalhou(true);
    }
    if (!silencioso && (k.status === 'rejected' || p.status === 'rejected')) toast.error('Erro ao carregar os números do período');
    setRecarregando(false);
  }, [periodo]);

  useEffect(() => {
    fetchPedidos();
    const id = setInterval(() => fetchPedidos(true), 30000);
    return () => clearInterval(id);
  }, [fetchPedidos]);

  useEffect(() => {
    fetchNumeros();
    const id = setInterval(() => fetchNumeros(true), 60000);
    return () => clearInterval(id);
  }, [fetchNumeros]);

  const atualizar = () => {
    fetchPedidos();
    fetchNumeros();
  };

  const figuras = kpis
    ? [
        { value: formatBRLCompacto(kpis.valor_total), label: 'vendidos no período' },
        { value: formatNumero(kpis.total), label: `${kpis.total === 1 ? 'pedido' : 'pedidos'}, ${formatNumero(kpis.enviados_tiny)} já no Tiny` },
        { value: formatBRLCompacto(kpis.valor_pago), label: `recebidos em ${plural(kpis.pagos, 'pedido', 'pedidos')}` },
        { value: formatBRLCompacto(kpis.valor_aberto), label: `a receber em ${plural(kpis.abertos, 'pedido', 'pedidos')}` },
        ...(kpis.erro_tiny > 0 ? [{ value: formatNumero(kpis.erro_tiny), label: 'com erro no Tiny' }] : []),
      ]
    : ['vendidos no período', 'pedidos', 'recebidos', 'a receber'].map((label) => ({ value: '—', label }));

  const gran = painel?.granularidade || 'dia';
  const nomeGran = NOME_GRANULARIDADE[gran];
  const melhor = painel?.serie?.reduce((m, s) => (s.valor > (m?.valor ?? 0) ? s : m), null);
  const totalEtapas = painel ? painel.etapas.reduce((s, e) => s + e.pedidos, 0) : 0;
  // Ao trocar o período, as folhas do painel ficam esmaecidas até o novo chegar (sem pulo de layout).
  const esmaece = `transition-opacity duration-150 ${recarregando && painel ? 'opacity-60' : ''}`;
  const semPainel = painelFalhou
    ? <p className="px-5 py-10 text-sm text-gray-500">Não foi possível carregar agora. Tente Atualizar.</p>
    : null;

  return (
    <div>
      <PageCover
        title="Dashboard"
        subtitle={`${veTodos ? 'Todos os pedidos' : 'Seus pedidos'}${lastUpdate ? ` · atualizado às ${formatHora(lastUpdate)}` : ''}`}
        actions={
          <>
            <Segmented options={PERIODOS_DASH} value={periodo} onChange={setPeriodo} label="Período" testid="kpi-periodo" onCover />
            <CoverButton onClick={atualizar} disabled={recarregando}>
              <RefreshCw className={recarregando ? 'animate-spin' : ''} />
              Atualizar
            </CoverButton>
          </>
        }
        figures={figuras}
      />
      {/* Faixas em vez de colunas soltas: cada faixa termina na mesma altura, seja qual for o perfil. A tabela por
          vendedor tem a faixa dela, na largura toda, para as nove colunas. */}
      <PageBody>
        <div className={`grid gap-5 ${COLUNAS}`}>
          <Card className={`flex flex-col overflow-hidden ${esmaece} ${isMaster ? 'xl:col-span-2' : 'xl:col-span-3'}`}>
            <SheetHeader title={`Vendas por ${nomeGran}`}>
              {melhor && (
                <span className="text-[12.5px] text-gray-500">
                  Melhor {nomeGran}: <span className="font-medium text-foreground">{rotuloCurto(melhor.chave, gran)}, {formatBRLCompacto(melhor.valor)}</span>
                </span>
              )}
            </SheetHeader>
            {painel ? <GraficoVendas serie={painel.serie} gran={gran} /> : semPainel || <Esqueleto altura="h-60" />}
          </Card>
          {isMaster && <SyncDimensoesCard />}
        </div>

        {veTodos && (
          <Card className={`overflow-hidden ${esmaece}`}>
            <SheetHeader title="Vendedores no período">
              {painel && (
                <span className="text-[12.5px] text-gray-500">{plural(painel.vendedores.filter((v) => v.nome).length, 'vendedor', 'vendedores')}</span>
              )}
            </SheetHeader>
            {painel ? <TabelaVendedores vendedores={painel.vendedores} /> : semPainel || <Esqueleto altura="h-48" />}
          </Card>
        )}

        <div className="grid gap-5 xl:grid-cols-2">
          <Card className={`flex flex-col overflow-hidden ${esmaece}`}>
            <SheetHeader title="Onde estão os pedidos">
              {painel && <span className="text-[12.5px] text-gray-500">{plural(totalEtapas, 'pedido', 'pedidos')}</span>}
            </SheetHeader>
            {painel ? <EtapasPedidos etapas={painel.etapas} /> : semPainel || <Esqueleto altura="h-40" />}
          </Card>
          <Card className="flex flex-col overflow-hidden">
            <SheetHeader title="Últimos pedidos">
              <Link
                to={isAdministrativo ? '/gestao-pedidos' : '/pedidos'}
                className="inline-flex items-center gap-1 text-[13px] font-medium text-blue-600 hover:underline dark:text-blue-400"
              >
                Ver todos <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </SheetHeader>
            <UltimosPedidos pedidos={pedidos.slice(0, 6)} />
          </Card>
        </div>
      </PageBody>
    </div>
  );
};

export default Dashboard;
